import { makeStyles } from '@fluentui/react-components'
import { useEffect, useRef, useState } from 'react'

const TOOLTIP_SHOW_DELAY = 500

const useStyles = makeStyles({
  // 1. pointerEvents: Fluent tooltips are hoverable by default. When one opens under (or next to)
  //    the cursor, the trigger gets a mouseleave and we hide it instantly. Letting the pointer
  //    pass through keeps the tooltip up for as long as the cursor stays on the trigger.
  // 2. visibility: Fluent also hides ("visibility: hidden") any tooltip that sits outside the
  //    clipping area of its trigger's overflow:hidden parents. File names are truncated inside
  //    such a parent, so the tooltip was mounted but invisible. We already control visibility
  //    ourselves, so we opt out of that automatic hiding. Scrolling is handled in the hook below.
  tooltipContent: { pointerEvents: 'none', visibility: 'visible' },
})

// Fluent UI's showDelay only applies to the first tooltip shown. Once any tooltip has been
// displayed, subsequent ones appear immediately due to an internal "browsing mode" being active.
// To work around this, we take full control of visibility using mouse events and a manual timer.
// We use mouse and focus events instead of onVisibleChange because when visible is controlled, Fluent UI
// fires onVisibleChange repeatedly while hovering, which prevents the hide logic from ever running.
// isTooltipVisibleRef tracks current visibility without stale closure issues inside setTimeout.
function useTooltipControl(showDelay = TOOLTIP_SHOW_DELAY) {
  const { tooltipContent: tooltipContentClassName } = useStyles()
  const [visibleTooltip, setVisibleTooltip] = useState<string | null>(null)
  const tooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isTooltipVisibleRef = useRef(false)

  useEffect(() => {
    return () => {
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current)
        tooltipTimerRef.current = null
      }
      isTooltipVisibleRef.current = false
    }
  }, [])

  // We opted out of Fluent's "hide when the trigger is scrolled out of view" (see styles above),
  // so we do it ourselves. Scroll events don't bubble, hence the capture listener on document.
  // This matters for keyboard focus: the trigger stays focused while the list scrolls away,
  // so neither blur nor mouseleave would ever close the tooltip.
  useEffect(() => {
    const dismissOnScroll = () => {
      if (!tooltipTimerRef.current && !isTooltipVisibleRef.current) return
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current)
        tooltipTimerRef.current = null
      }
      isTooltipVisibleRef.current = false
      setVisibleTooltip(null)
    }
    document.addEventListener('scroll', dismissOnScroll, { capture: true, passive: true })
    return () => document.removeEventListener('scroll', dismissOnScroll, { capture: true })
  }, [])

  const handleMouseEnter = (itemValue: string) => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current)
      tooltipTimerRef.current = null
    }
    const delay = isTooltipVisibleRef.current ? 0 : showDelay
    tooltipTimerRef.current = setTimeout(() => {
      isTooltipVisibleRef.current = true
      setVisibleTooltip(itemValue)
    }, delay)
  }

  const handleMouseLeave = (itemValue: string) => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current)
      tooltipTimerRef.current = null
    }
    isTooltipVisibleRef.current = false
    setVisibleTooltip((current) => (current === itemValue ? null : current))
  }

  const handleFocus = (itemValue: string) => {
    handleMouseEnter(itemValue)
  }

  const handleBlur = (itemValue: string) => {
    handleMouseLeave(itemValue)
  }

  // Unconditionally clears any pending timer and hides any visible tooltip.
  // Use this when the tooltip should disappear regardless of which item triggered it
  // (e.g. opening the file-action menu, where focus events would otherwise re-show it).
  const clearTooltip = () => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current)
      tooltipTimerRef.current = null
    }
    isTooltipVisibleRef.current = false
    setVisibleTooltip(null)
  }

  return {
    visibleTooltip,
    handleMouseEnter,
    handleMouseLeave,
    handleFocus,
    handleBlur,
    clearTooltip,
    tooltipContentClassName,
  }
}

export { useTooltipControl }
